import { useEffect, useState } from 'react';

import { useAtom } from 'jotai';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import useAuth from '@/hooks/useAuth';
import { sendNativeMessage } from '@/hooks/useNativeListener';

import { getLaunchdDarklyUser } from '@/utils/user';

import { appInitializedAtom } from './state';

import { env } from '@/env';

const isDevEnvironment = env.REACT_APP_ENV === 'dev';

const LaunchDarklySetup: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: auth } = useAuth();
  const [isInitialized] = useAtom(appInitializedAtom);
  const ldClient = useLDClient();
  const isInternational = auth?.isInternationalUser;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!ldClient || !auth || !isInitialized) return;

    const logDevAllFlags = (label: string) => {
      if (isDevEnvironment) {
        console.log(`[LaunchDarkly] ${label}`, ldClient.allFlags());
      }
    };
    const onFlagsChanged = () => {
      if (!cancelled) logDevAllFlags('flags updated (identify or stream)');
    };
    if (isDevEnvironment) ldClient.on('change', onFlagsChanged);

    const markReady = (label: string) => {
      if (cancelled) return;
      logDevAllFlags(label);
      setReady(true);
    };

    const raceWithTimeout = async (promise: Promise<unknown>) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<void>(resolve => {
        timeoutId = setTimeout(resolve, 15_000);
      });

      try {
        await Promise.race([promise, timeoutPromise]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    void (async () => {
      const ldUser = getLaunchdDarklyUser(auth);
      if (isInternational) {
        await raceWithTimeout(
          ldClient.identify(ldUser).catch((error: unknown) => {
            console.error('LaunchDarkly identify failed', error);
          }),
        );
        markReady('flags after identify (international)');
        return;
      }
      sendNativeMessage({ type: 'updateUser', payload: { user: auth } });
      if (ldUser.email) {
        await raceWithTimeout(
          ldClient.identify(ldUser).catch((error: unknown) => {
            console.error('LaunchDarkly identify failed', error);
          }),
        );
      }
      markReady(
        ldUser.email
          ? 'flags after identify'
          : 'flags (still anonymous context; user has no email)',
      );
    })();

    return () => {
      cancelled = true;
      if (isDevEnvironment) ldClient.off('change', onFlagsChanged);
    };
  }, [isInitialized, isInternational, ldClient, auth]);

  return ready ? children : null;
};

export default LaunchDarklySetup;
